import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { requestStatus } from '../components/AdminAccessTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span><span className="font-medium text-[#283880] text-right break-all">{value || '-'}</span>
  </div>
);

export default function AccessRequestReview() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token');
  const suggested = params.get('azione');
  const [req, setReq] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    axios.get(`${API}/access-requests/${id}`, { params: { token } })
      .then((r) => setReq(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Link non valido'));
  }, [id, token]);

  const decide = async (action) => {
    setBusy(action);
    setError('');
    try {
      const r = await axios.post(`${API}/access-requests/${id}/decision`, { token, action });
      setReq({ ...req, status: r.data.status });
    } catch (e) {
      setError(e.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setBusy('');
    }
  };

  const st = req && (requestStatus[req.status] || requestStatus.in_attesa);
  return (
    <div className="min-h-screen bg-slate-50 pt-32 pb-16 px-4">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-sm p-6 md:p-8" data-testid="access-review-page">
        <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#283880] mb-1">Richiesta di accesso</h1>
        <p className="text-sm text-slate-500 mb-6">Richiesta inviata tramite l'assistente virtuale del sito.</p>
        {!req && !error && <Loader2 className="w-6 h-6 animate-spin text-[#283880]" />}
        {req && (
          <>
            <Field label="Nome" value={req.name} />
            <Field label="Codice fiscale / P.IVA" value={req.fiscal_id} />
            <Field label="Email (verificata)" value={req.email} />
            <Field label="Telefono" value={req.phone} />
            <Field label="N. pratica indicato" value={req.numero_pratica} />
            <div className="mt-4 mb-6"><span className={`text-xs px-3 py-1 rounded-full ${st.cls}`} data-testid="access-review-status">{st.label}</span></div>
            {req.status === 'in_attesa' && (
              <div className="flex flex-col sm:flex-row gap-3">
                <Button disabled={!!busy} onClick={() => decide('approva')} className={`flex-1 bg-green-600 hover:bg-green-700 ${suggested === 'approva' ? 'ring-2 ring-green-300' : ''}`} data-testid="access-review-approve">
                  {busy === 'approva' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}Approva e crea area clienti
                </Button>
                <Button disabled={!!busy} variant="outline" onClick={() => decide('rifiuta')} className={`flex-1 text-red-600 border-red-200 ${suggested === 'rifiuta' ? 'ring-2 ring-red-200' : ''}`} data-testid="access-review-reject">
                  <XCircle className="w-4 h-4 mr-2" />Rifiuta
                </Button>
              </div>
            )}
            {req.status === 'approvata' && <p className="text-sm text-green-700" data-testid="access-review-done">Area clienti creata. Il cliente ha ricevuto l'email per impostare la password. Ora può collegare le sue pratiche dal pannello admin.</p>}
            {req.status === 'rifiutata' && <p className="text-sm text-slate-600" data-testid="access-review-done">Richiesta rifiutata. Il richiedente è stato avvisato via email.</p>}
          </>
        )}
        {error && <p className="text-sm text-red-600 mt-4" data-testid="access-review-error">{error}</p>}
      </div>
    </div>
  );
}
